import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API_URL } from './AdminShared';
import {
  Compass, Loader2, Send, MapPin, Home, TrendingUp, Award, RefreshCw, Filter
} from 'lucide-react';

const fmtINR = (n) => {
  if (!n) return '—';
  if (n >= 1e7) return `₹${(n/1e7).toFixed(2)} Cr`;
  if (n >= 1e5) return `₹${(n/1e5).toFixed(1)} L`;
  return `₹${n.toLocaleString()}`;
};

const Stat = ({ label, value, sub, icon, accent='text-[#E07A3A]' }) => (
  <div className="bg-white border border-slate-200 px-5 py-4">
    <div className="flex items-center justify-between mb-1">
      <span className="text-xs tracking-[0.2em] uppercase text-slate-500">{label}</span>
      <span className={accent}>{icon}</span>
    </div>
    <div className="font-['Playfair_Display'] text-3xl text-[#1A1A1A]">{value ?? '—'}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const ScoreBar = ({ score }) => {
  const colour = score >= 75 ? 'bg-emerald-600' : score >= 50 ? 'bg-amber-500' : 'bg-slate-400';
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-slate-100">
        <div className={`h-full ${colour}`} style={{ width: `${Math.min(100, score)}%` }} />
      </div>
      <span className="text-xs font-medium tabular-nums text-slate-700 w-7">{score}</span>
    </div>
  );
};

export const AdminNorthIndia = () => {
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [running, setRunning] = useState(false);
  const [filterCity, setFilterCity] = useState('');
  const [minScore, setMinScore] = useState(0);

  const fetchStats = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/north-india/stats`);
      setStats(r.data);
    } catch { /* silent */ }
  }, []);

  const fetchLeads = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '100', min_score: String(minScore) });
      if (filterCity) params.append('city', filterCity);
      const r = await axios.get(`${API_URL}/api/admin/north-india/leads?${params}`);
      setLeads(r.data.items || []);
    } catch { toast.error('Failed to load warm leads'); }
  }, [filterCity, minScore]);

  useEffect(() => { fetchStats(); fetchLeads(); }, [fetchStats, fetchLeads]);

  const runScan = async () => {
    if (running) return;
    setRunning(true);
    toast.info('Scanning Chandigarh / Mohali / Jalandhar sectors — 2-3 min', { duration: 8000 });
    try {
      const r = await axios.post(`${API_URL}/api/admin/north-india/scan`, {});
      const s = r.data;
      toast.success(
        `Scan complete: ${s.warm_leads_new} new leads from ${s.sectors_scanned} sectors (${s.candidates_found} candidates)`,
        { duration: 8000 }
      );
      fetchStats(); fetchLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Scan failed');
    } finally { setRunning(false); }
  };

  const sendPulse = async (id) => {
    try {
      await axios.post(`${API_URL}/api/admin/north-india/leads/${id}/pulse`);
      toast.success('Private Pulse email queued via Resend');
      fetchLeads(); fetchStats();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Pulse send failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-north-india">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="text-xs tracking-[0.3em] uppercase text-[#E07A3A] block mb-1 font-medium">Shopperden North India Hub</span>
          <h2 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">Predictive Listing Lens AI</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Targets Chandigarh sectors 1-60, Mohali (82, IT City, Airport Rd) and Jalandhar
            (Model Town, Urban Estate, Pholriwal). Sellability Score uses price decile,
            sector premium and listing freshness. P2P data gateway — buyers conduct independent
            legal due diligence locally.
          </p>
        </div>
        <button onClick={runScan} disabled={running}
          className="px-5 py-2.5 text-sm tracking-wider uppercase font-semibold bg-[#E07A3A] text-white hover:bg-[#C4652E] disabled:opacity-50 flex items-center gap-2"
          data-testid="btn-run-scan">
          {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Compass className="h-4 w-4" />}
          {running ? 'Scanning…' : 'Run Predictive Scan'}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="Total Leads" value={stats?.total_leads} icon={<Home className="h-4 w-4" />} />
        <Stat label="High Score (≥75)" value={stats?.high_score} sub="Hot prospects" accent="text-emerald-600" icon={<Award className="h-4 w-4" />} />
        <Stat label="Medium (50-74)" value={stats?.medium_score} accent="text-amber-600" icon={<TrendingUp className="h-4 w-4" />} />
        <Stat label="Pulses Sent" value={stats?.pulse_emails_sent} sub="Magic-link emails" icon={<Send className="h-4 w-4" />} />
        <Stat label="Sectors" value={stats?.sector_count} sub="In scope" icon={<MapPin className="h-4 w-4" />} />
      </div>

      {stats?.by_city && Object.keys(stats.by_city).length > 0 && (
        <div className="bg-amber-50 border border-amber-200 px-4 py-2 flex flex-wrap gap-3">
          <span className="text-xs tracking-[0.2em] uppercase text-amber-800">By city:</span>
          {Object.entries(stats.by_city).map(([c, n]) => (
            <button key={c} onClick={() => setFilterCity(filterCity === c ? '' : c)}
              className={`text-xs px-2 py-0.5 ${filterCity === c ? 'bg-[#E07A3A] text-white' : 'bg-white text-slate-700 border border-amber-300'}`}>
              {c}: {n}
            </button>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-slate-400" />
          <span className="text-xs tracking-wider uppercase text-slate-500">Score ≥</span>
          {[0, 50, 75].map(t => (
            <button key={t} onClick={() => setMinScore(t)}
              className={`text-xs px-3 py-1 ${minScore === t ? 'bg-[#0F172A] text-white' : 'border border-slate-300 text-slate-600'}`}>
              {t === 0 ? 'All' : t}
            </button>
          ))}
        </div>
        <button onClick={fetchLeads} className="ml-auto text-slate-400 hover:text-slate-600">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      <section className="bg-white border border-slate-200">
        <header className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <span className="font-semibold text-sm tracking-wide">Warm Leads ({leads.length})</span>
          <span className="text-xs text-slate-400">Sorted by Sellability Score</span>
        </header>
        <div className="divide-y divide-slate-100 max-h-[700px] overflow-auto">
          {leads.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-400">
              No leads yet. Click <strong>Run Predictive Scan</strong> above.
            </div>
          )}
          {leads.map(l => (
            <div key={l.id} className="px-5 py-3.5 grid grid-cols-12 gap-3 items-center" data-testid={`lead-${l.id}`}>
              <div className="col-span-12 md:col-span-5">
                <a href={l.listing_url} target="_blank" rel="noreferrer"
                  className="font-medium text-sm text-[#1A1A1A] hover:text-[#E07A3A] truncate block">
                  {l.title}
                </a>
                <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5 flex-wrap">
                  <span><MapPin className="h-3 w-3 inline mr-0.5" />{l.city}</span>
                  <span className="bg-slate-100 px-1.5 py-0.5">Sector {l.sector}</span>
                  {l.tier === 'posh' && <span className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5">Posh</span>}
                  {l.bedrooms ? <span>{l.bedrooms} BHK</span> : null}
                </div>
              </div>
              <div className="col-span-6 md:col-span-2 text-sm font-semibold text-[#E07A3A]">
                {l.asking_price_raw || fmtINR(l.asking_price_inr)}
              </div>
              <div className="col-span-6 md:col-span-3"><ScoreBar score={l.sellability_score || 0} /></div>
              <div className="col-span-12 md:col-span-2 flex justify-end">
                {l.pulse_email_sent_at ? (
                  <span className="text-[10px] tracking-wider uppercase text-emerald-700">Pulse sent ✓</span>
                ) : l.email ? (
                  <button onClick={() => sendPulse(l.id)}
                    className="px-3 py-1.5 text-xs tracking-wider uppercase font-semibold bg-[#0F172A] text-white hover:bg-[#1f2937] flex items-center gap-1"
                    data-testid={`btn-pulse-${l.id}`}>
                    <Send className="h-3 w-3" />Pulse
                  </button>
                ) : (
                  <span className="text-[10px] text-slate-400">No email</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      <div className="bg-slate-50 border border-slate-200 px-5 py-4 text-xs text-slate-600 leading-relaxed">
        <strong>Data Gateway Disclaimer (P2P model):</strong> Shopperden indexes publicly
        available property listings. We do not verify ownership documents or RERA
        registration on behalf of buyers. Buyers in Chandigarh / Mohali / Jalandhar are
        responsible for independent legal due diligence via locally-registered advocates.
        Listings are surfaced from open OLX / NoBroker / housing.com inventory under
        DPDP §7(b) "publicly available data" basis.
      </div>
    </div>
  );
};
